import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import ReportToolbar from './ReportToolbar';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const PurchaseOrderWiseReport = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch]     = useState('');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'PurchaseReportApi',
        `/PurchaseOrderWiseDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
      setExpanded({});
    } catch (err) {
      console.error(err);
      setError('Failed to fetch purchase order wise report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]);

  // Group flat rows by PurchaseOrderNo — all rows with same PO number become one entry
  const grouped = (() => {
    const map = new Map();
    data.forEach(row => {
      const poNo = (row.PurchaseOrderNo || '').trim();
      if (!poNo && row.ItemName && map.size > 0) {
        // Row without PO number — belongs to last added group
        const lastKey = [...map.keys()].pop();
        map.get(lastKey).items.push({
          ItemName: row.ItemName,
          Quantity: row.Quantity,
          Rate: row.Rate,
          GSTRate: row.GSTRate,
          TaxableAmount: row.TaxableAmount,
          ItemGST: row.ItemGST,
          TotalAmount: row.TotalAmount,
        });
      } else if (poNo) {
        if (map.has(poNo)) {
          // Same PO — add as item
          map.get(poNo).items.push({
            ItemName: row.ItemName,
            Quantity: row.Quantity,
            Rate: row.Rate,
            GSTRate: row.GSTRate,
            TaxableAmount: row.TaxableAmount,
            ItemGST: row.ItemGST,
            TotalAmount: row.TotalAmount,
          });
        } else {
          // New PO — create master entry
          map.set(poNo, {
            Id: row.Id,
            PurchaseOrderNo: poNo,
            PurchaseOrderDate: row.PurchaseOrderDate,
            MasterName: row.MasterName,
            TotalQuantity: row.TotalQuantity,
            PO_TaxableAmount: row.PO_TaxableAmount,
            PO_TotalGST: row.PO_TotalGST,
            PO_TotalAmount: row.PO_TotalAmount,
            IsConverted: row.IsConverted,
            IsVerified: row.IsVerified,
            items: row.ItemName ? [{
              ItemName: row.ItemName,
              Quantity: row.Quantity,
              Rate: row.Rate,
              GSTRate: row.GSTRate,
              TaxableAmount: row.TaxableAmount,
              ItemGST: row.ItemGST,
              TotalAmount: row.TotalAmount,
            }] : [],
          });
        }
      }
    });
    return [...map.values()];
  })();

  const masters = grouped;
  console.log('[PurchaseOrderWiseReport] Raw data rows:', data.length, '| Grouped POs:', grouped.length, '| Sample:', data[0]);

  const filtered = search.trim()
    ? masters.filter(m =>
        (m.PurchaseOrderNo || '').toLowerCase().includes(search.toLowerCase()) ||
        (m.MasterName      || '').toLowerCase().includes(search.toLowerCase())
      )
    : masters;

  const toggleExpand = (poNo) => setExpanded(prev => ({ ...prev, [poNo]: !prev[poNo] }));

  // Totals
  const totalTaxable = filtered.reduce((s, r) => s + (parseFloat(r.PO_TaxableAmount) || 0), 0);
  const totalGST     = filtered.reduce((s, r) => s + (parseFloat(r.PO_TotalGST)      || 0), 0);
  const totalAmount  = filtered.reduce((s, r) => s + (parseFloat(r.PO_TotalAmount)    || 0), 0);
  const totalQty     = filtered.reduce((s, r) => s + (parseFloat(r.TotalQuantity)     || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'PurchaseOrderWiseReport', 0);
    if (!filtered.length) return;
    const now = new Date();
    const generatedAt = now.toLocaleString('en-IN');
    const colHeaders = ['#', 'PO No', 'Date', 'Supplier', 'Total Qty', 'Taxable Amt', 'GST', 'Total Amt', 'Converted', 'Verified', '', 'Item Name', 'Qty', 'Rate', 'GST%', 'Taxable', 'Item GST', 'Item Total'];
    const rows = [];
    filtered.forEach((m, i) => {
      rows.push([
        i + 1, m.PurchaseOrderNo, fmtDate(m.PurchaseOrderDate), m.MasterName,
        parseFloat(m.TotalQuantity) || 0,
        parseFloat(m.PO_TaxableAmount) || 0,
        parseFloat(m.PO_TotalGST) || 0,
        parseFloat(m.PO_TotalAmount) || 0,
        m.IsConverted === 'Yes' || m.IsConverted === true ? 'Yes' : 'No',
        m.IsVerified  === 'Yes' || m.IsVerified  === true ? 'Yes' : 'No',
        '', '', '', '', '', '', '', '',
      ]);
      m.items.forEach(it => {
        rows.push([
          '', '', '', '', '', '', '', '', '', '', '',
          it.ItemName, parseFloat(it.Quantity) || 0, parseFloat(it.Rate) || 0,
          parseFloat(it.GSTRate) || 0, parseFloat(it.TaxableAmount) || 0,
          parseFloat(it.ItemGST) || 0, parseFloat(it.TotalAmount) || 0,
        ]);
      });
    });

      await exportStyledExcel({
          reportTitle: 'PURCHASE ORDER W ISE REPORT',
          sheetName: 'PO Wise Report',
          fileName: `PurchaseOrderWiseReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows,
          totalsRow: ['', '', '', 'Totals', totalQty, totalTaxable, totalGST, totalAmount, '', '', '', '', '', '', '', '', '', ''],
      });
  };

  const PRIMARY = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT = '#e0f7fa';

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading purchase order wise report..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <ReportToolbar
          title="Purchase Order Wise Report"
          onBack={() => history.push('/ReportsHub')}
          search={search}
          onSearchChange={setSearch}
          fromDate={fromDate}
          onFromDate={setFromDate}
          toDate={toDate}
          onToDate={setToDate}
          onRefresh={fetchReport}
          loading={loading}
          onExport={handleExport}
          exportDisabled={!filtered.length}
          searchPlaceholder="Search PO, supplier..."
        />

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: 900 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={{ ...th, width: 32 }}></th>
                <th style={th}>#</th>
                <th style={th}>PO No</th>
                <th style={th}>Date</th>
                <th style={th}>Supplier</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxable Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>GST</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Amt</th>
                <th style={{ ...th, textAlign: 'center' }}>Converted</th>
                <th style={{ ...th, textAlign: 'center' }}>Verified</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched ? 'No records found for the selected date range' : 'Select date range and click refresh to load report'}
                  </td>
                </tr>
              ) : (
                filtered.map((m, i) => {
                  const isOpen = !!expanded[m.PurchaseOrderNo];
                  const isConverted = m.IsConverted === 'Yes' || m.IsConverted === true;
                  const isVerified  = m.IsVerified  === 'Yes' || m.IsVerified  === true;
                  return (
                    <>
                      {/* Master Row */}
                      <tr
                        key={m.PurchaseOrderNo}
                        onClick={() => toggleExpand(m.PurchaseOrderNo)}
                        style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                      >
                        <td style={{ ...tdC, width: 32 }}>
                          {isOpen
                            ? <KeyboardArrowDown sx={{ fontSize: 16, color: PRIMARY }} />
                            : <KeyboardArrowRight sx={{ fontSize: 16, color: '#9ca3af' }} />}
                        </td>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{m.PurchaseOrderNo}</td>
                        <td style={td}>{fmtDate(m.PurchaseOrderDate)}</td>
                        <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.MasterName}</td>
                        <td style={tdR}>{fmtQty(m.TotalQuantity)}</td>
                        <td style={tdR}>{fmt(m.PO_TaxableAmount)}</td>
                        <td style={tdR}>{fmt(m.PO_TotalGST)}</td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmt(m.PO_TotalAmount)}</td>
                        <td style={tdC}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: isConverted ? '#d1fae5' : '#f3f4f6', color: isConverted ? '#059669' : '#6b7280' }}>
                            {isConverted ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={tdC}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: isVerified ? '#d1fae5' : '#f3f4f6', color: isVerified ? '#059669' : '#6b7280' }}>
                            {isVerified ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>

                      {/* Item Sub-Header */}
                      {isOpen && (
                        <tr style={{ background: '#c7d2fe', borderBottom: '1px solid #a5b4fc' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>#</td>
                          <td colSpan={3} style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b', paddingLeft: 24 }}>Item Name</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Quantity</td>
                          <td colSpan={2} style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Rate / GST%</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Taxable Amt</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Item GST</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Total Amt</td>
                        </tr>
                      )}

                      {/* Item Rows */}
                      {isOpen && m.items.map((it, j) => (
                        <tr key={`${m.PurchaseOrderNo}-${j}`} style={{ background: '#eef2ff', borderBottom: '1px solid #e0e7ff' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, color: '#6b7280' }}>{j + 1}</td>
                          <td colSpan={3} style={{ ...td, fontWeight: 600, color: '#374151', paddingLeft: 24 }}>{it.ItemName}</td>
                          <td style={tdR}>{fmtQty(it.Quantity)}</td>
                          <td colSpan={2} style={tdR}>{fmt(it.Rate)} / {it.GSTRate}%</td>
                          <td style={tdR}>{fmt(it.TaxableAmount)}</td>
                          <td style={tdR}>{fmt(it.ItemGST)}</td>
                          <td style={{ ...tdR, fontWeight: 600 }}>{fmt(it.TotalAmount)}</td>
                        </tr>
                      ))}
                    </>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalQty)}</td>
                  <td style={tdR}>{fmt(totalTaxable)}</td>
                  <td style={tdR}>{fmt(totalGST)}</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td colSpan={2} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderWiseReport;


